import { useTranslation } from "react-i18next";
import React, { useEffect, useRef, useState } from "react";
import { ComboBox, Item } from "@adobe/react-spectrum";
import { useAsyncList } from "@react-stately/data";
import { useDependency } from "../../../../../../contexts/DependencyProvider";
import { ListFPARequest2 } from "../../../../../../services/soap/project/requests/ListFPARequest2";

interface Customer {
  id: string;
  folderType: string;
  folderTypeName: string;
  referenceId: string;
  name: string;
  customState: string;
  customStateName: string;
  organization: string;
  created: string;
  createdByName: string;
  modified: string;
  modifiedByName: string;
  state: string;
  contextParent: string;
  contextParentName: string;
}

interface ICustomerDroDown {
  selectedCustomer: string;
  CustomerName?: string;
  onChange: (data: Customer | null) => void; // ✅ allow null when cleared
  isEdit?: any;
  isRequired?: any;
  errorMessage?: any;
  validationState?: any;
  width?: any;
  label?: any;
}

const CustomerDroDown: React.FC<ICustomerDroDown> = ({
  selectedCustomer,
  CustomerName,
  onChange,
  isEdit,
  isRequired,
  errorMessage,
  validationState,
  width,
  label,
}) => {
  const { t } = useTranslation("invoice");
  const _t = (key: string) => t(`add_update_invoice.${key}`, { ns: "invoice" });

  const { store, projectService } = useDependency();

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>("");

  // keep your "initialLoad uses CustomerName" behavior
  const initialLoadRef = useRef(true);

  // prevent parent sync fighting while user types
  const isUserEditingRef = useRef(false);

  // if you still want "auto select 0th item" ONCE (only when not edit and no selectedCustomer)
  const didInitialAutoSelectRef = useRef(false);

  const list = useAsyncList<Customer>({
    async load({ cursor, filterText }) {
      const offset = cursor ? Number(cursor) : 0;
      const limit = 5000;

      try {
        const requestBody: any = {
          limit,
          offset,
          flatView: true,
          folderFilter: {
            COL: [
              {
                name: "fpaTypeCategories",
                type: "any",
                value: "com.atollon.invoice.fpatypecategory.client",
              },
            ],
          },
          ORDER: {
            COL: [
              { name: "name", desc: false },
              { name: "created", desc: true },
            ],
          },
        };

        // ✅ keep your server-side search logic
        if (
          initialLoadRef.current &&
          CustomerName &&
          CustomerName.trim() !== "" &&
          !isEdit
        ) {
          requestBody.fulltext = CustomerName + "*";
        } else if (filterText?.trim()) {
          requestBody.fulltext = filterText + "*";
        }

        const response = await projectService.listFPA2(
          new ListFPARequest2(store.Server, store.SessionId, requestBody)
        );

        const folders: Customer[] = response?.ITEMS
          ? response.ITEMS.map((entry: any) => entry.ITEM?.FOLDER).filter(Boolean)
          : [];
        console.log("folders", selectedCustomer)
        console.table(folders)

        if (!Array.isArray(folders)) return { items: [] };

        // ✅ only mark initial load as complete AFTER first load
        if (initialLoadRef.current) {
          initialLoadRef.current = false;
        }

        return {
          items: folders,
          cursor: folders.length === limit ? String(offset + limit) : undefined,
        };
      } catch (error) {
        console.error("Error fetching Customers:", error);
        return { items: [] };
      }
    },
  });

  // initial fetch
  useEffect(() => {
    list.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ auto-select 0th item ONCE (only when not edit and nothing selected from parent)
  useEffect(() => {
    if (
      didInitialAutoSelectRef.current ||
      isEdit ||
      selectedCustomer ||
      list.items.length === 0
    ) {
      return;
    }

    didInitialAutoSelectRef.current = true;

    const first = list.items[0];
    setSelectedKey(String(first.id));
    setInputValue(first.name ?? "");
    onChange(first);
  }, [list.items, isEdit, selectedCustomer, onChange]);

  // ✅ sync from parent ONLY when user not typing
  useEffect(() => {
    if (isUserEditingRef.current) return;

    if (!selectedCustomer) {
      setSelectedKey(null);
      // if you want input to show CustomerName when adding, keep it; otherwise empty:
      setInputValue("");
      return;
    }

    setSelectedKey(String(selectedCustomer));
    const selected = list.items.find((x) => String(x.id) === String(selectedCustomer));
    setInputValue(selected?.name ?? "");
  }, [selectedCustomer, list.items]);

  const handleInputChange = (val: string) => {
    isUserEditingRef.current = true;

    setInputValue(val);
    list.setFilterText(val);

    // ✅ allow backspace: if text diverges from selected label, clear selection
    if (selectedKey) {
      const selected = list.items.find((x) => String(x.id) === String(selectedKey));
      const selectedLabel = selected?.name ?? "";
      if (val !== selectedLabel) {
        setSelectedKey(null);
      }
    }

    // ✅ cleared => empty dropdown
    if (!val.trim()) {
      setSelectedKey(null);
      list.setFilterText("");
      onChange(null);
    }
  };

  const handleSelectionChange = (key: React.Key | null) => {
    isUserEditingRef.current = false;

    const idStr = key ? String(key) : null;
    setSelectedKey(idStr);

    if (!idStr) return;

    const selected = list.items.find((x) => String(x.id) === idStr);
    if (selected) {
      setInputValue(selected.name ?? "");
      onChange(selected);
      list.setFilterText(""); // next focus shows all
    }
  };

  return (
    <ComboBox
      label={label ? label : _t("customer")}
      aria-label={label ? label : _t("customer")}
      items={list.items}
      selectedKey={selectedKey}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      loadingState={list.loadingState}
      onLoadMore={list.loadMore}
      onSelectionChange={handleSelectionChange}
      allowsCustomValue
      width={{ base: "100%", L: width ? width : "100%" }}
      isRequired={isRequired}
      validationState={validationState}
      errorMessage={errorMessage}
      onFocus={() => {
        isUserEditingRef.current = true;
        list.setFilterText("");
      }}
      onBlur={() => {
        isUserEditingRef.current = false;
      }}
    >
      {(item) => (
        <Item key={item.id} textValue={item.name}>
          {item.name}
        </Item>
      )}
    </ComboBox>
  );
};

export default CustomerDroDown;
